<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isWhatsAppType: {
    type: Boolean,
    default: false,
  },
  deliverySummary: {
    type: Object,
    default: () => ({}),
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['edit', 'delete', 'details']);

const { t } = useI18n();
const detailsTitle = 'Detalhar entregas';

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (props.status === STATUS_COMPLETED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }

  if (props.status === STATUS_PROCESSING) {
    return t('CAMPAIGN.SMS.CARD.STATUS.PROCESSING');
  }

  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
  } = props.inbox;
  return getInboxIconByType(type, medium, 'fill', voiceEnabled);
});
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-if="isWhatsAppType && deliverySummary.total"
        class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-n-slate-11"
      >
        <span class="font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.TOTAL') }}
          {{ deliverySummary.total }}
        </span>
        <span class="text-n-blue-11">
          {{ t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.ACCEPTED') }}
          {{ deliverySummary.accepted }}
        </span>
        <span class="text-n-cyan-11">
          {{ t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.SENT') }}
          {{ deliverySummary.sent }}
        </span>
        <span class="text-n-teal-11">
          {{ t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.DELIVERED') }}
          {{ deliverySummary.delivered }}
        </span>
        <span class="text-n-violet-11">
          {{ t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.READ') }}
          {{ deliverySummary.read }}
        </span>
        <span v-if="deliverySummary.failed" class="text-n-ruby-11">
          {{ t('CAMPAIGN.WHATSAPP.CARD.DELIVERY.FAILED') }}
          {{ deliverySummary.failed }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
    </div>
    <div class="flex items-center justify-end gap-2 min-w-20">
      <Button
        v-if="isWhatsAppType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-chart-no-axes-column-increasing"
        :title="detailsTitle"
        @click="emit('details')"
      />
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
